import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { Label } from '@shared/components/ui/shadcn/label';

export interface BulkPreparationModalRef {
  open: (count: number) => void;
  close: () => void;
}

interface BulkPreparationModalProps {
  onConfirm?: (preparationType: 'picking_packing' | 'massive_packing') => void;
  onCancel?: () => void;
}

export const BulkPreparationModal = forwardRef<BulkPreparationModalRef, BulkPreparationModalProps>(
  ({ onConfirm, onCancel }, ref) => {
    const { t } = useTranslation('ordersModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [orderCount, setOrderCount] = useState(0);
    const [preparationType, setPreparationType] = useState<'picking_packing' | 'massive_packing'>('picking_packing');
    const [isProcessing, setIsProcessing] = useState(false);

    useImperativeHandle(ref, () => ({
      open: (count: number) => {
        setOrderCount(count);
        setPreparationType('picking_packing'); // Reset to default
        setIsProcessing(false); // Reset processing state
        modalRef.current?.openModal();
      },
      close: () => {
        setIsProcessing(false);
        modalRef.current?.closeModal();
      },
    }));

    const handleConfirm = () => {
      if (isProcessing) return; // Prevent multiple clicks

      setIsProcessing(true);
      try {
        onConfirm?.(preparationType);
        modalRef.current?.closeModal();
      } catch (error) {
        setIsProcessing(false);
      }
    };

    const handleCancel = () => {
      onCancel?.();
      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter ref={modalRef} title={t('confirmModals.bulkPreparation.title')} type="default">
        <div className="space-y-6">
          <p className="text-sm text-subtext">{t('confirmModals.bulkPreparation.message', { count: orderCount })}</p>

          <RadioGroup
            value={preparationType}
            onValueChange={(value) => setPreparationType(value as 'picking_packing' | 'massive_packing')}
            className="flex flex-col gap-3"
            disabled={isProcessing}
          >
            <label
              htmlFor="picking_packing"
              className="flex items-center space-x-2 border border-card-border p-3 rounded-md hover:bg-card-bg cursor-pointer transition-colors"
            >
              <RadioGroupItem value="picking_packing" id="picking_packing" />
              <span className="flex-1 font-medium select-none pointer-events-none">
                {t('confirmModals.bulkPreparation.options.picking_packing')}
              </span>
            </label>
            <label
              htmlFor="massive_packing"
              className="flex items-center space-x-2 border border-card-border p-3 rounded-md hover:bg-card-bg cursor-pointer transition-colors"
            >
              <RadioGroupItem value="massive_packing" id="massive_packing" />
              <span className="flex-1 font-medium select-none pointer-events-none">
                {t('confirmModals.bulkPreparation.options.massive_packing')}
              </span>
            </label>
          </RadioGroup>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="lg" onClick={handleCancel}>
              {t('buttons.cancel')}
              <EscIcon className="w-4 h-4" />
            </Button>
            <Button variant="primary" size="lg" onClick={handleConfirm} disabled={isProcessing}>
              {t('buttons.confirm')}
              <EnterIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

BulkPreparationModal.displayName = 'BulkPreparationModal';
